import { Anchor, Avatar, Badge, Group, Stack, Text, Tooltip } from '@mantine/core';
import { IconAlertCircle, IconPlayerPause, IconWallet } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { i18nKeys, useTranslation } from 'locales';
import type { MouseEvent } from 'react';
import { Link } from 'react-router-dom';

import { useClientHoverCardData } from './useClientHoverCardData';

type ClientHoverCardHeaderProps = {
  debtorId: string;
  debtorName: string;
};

export const ClientHoverCardHeader = ({ debtorId, debtorName }: ClientHoverCardHeaderProps) => {
  const { t } = useTranslation();
  const { debtor } = useClientHoverCardData(debtorId);

  return (
    <Group gap="sm" wrap="nowrap">
      <Avatar name={debtorName} size={36} radius="xl" color="violet" />
      <Stack gap={4}>
        <Anchor component={Link} to={`/clients/${debtorId}`} fw={600} c="inherit">
          {debtorName}
        </Anchor>
        <Group gap={4}>
          {debtor?.paused && (
            <Tooltip
              label={
                <Stack gap={0}>
                  <Text>
                    {t(i18nKeys.REASON)}: {debtor.reason}
                  </Text>
                  {debtor.end_pause_at && (
                    <Text>
                      {t(i18nKeys.END_OF_PAUSE)}: {dayjs(debtor.end_pause_at).format('L')}
                    </Text>
                  )}
                </Stack>
              }
            >
              <Badge
                leftSection={<IconPlayerPause size={12} stroke={1.5} />}
                size="xs"
                variant="light"
                color={'warning'}
              >
                {`${t(i18nKeys.CLIENT.STATUS.PAUSED)}${debtor.end_pause_at ? ` · ${dayjs(debtor.end_pause_at).format('L')}` : ''}`}
              </Badge>
            </Tooltip>
          )}
          {debtor?.disputed && (
            <Tooltip label={`${t(i18nKeys.REASON)}: ${debtor.reason}`}>
              <Badge
                leftSection={<IconAlertCircle size={12} stroke={1.5} />}
                size="xs"
                variant="light"
                color="error"
              >
                {t(i18nKeys.CLIENT.STATUS.DISPUTE)}
              </Badge>
            </Tooltip>
          )}
          {debtor?.has_payment_plan && (
            <Badge
              component={Link}
              to={`/payment-plans?debtor_id=${debtorId}&status=in_progress`}
              onClick={(event: MouseEvent<HTMLAnchorElement>) => event.stopPropagation()}
              leftSection={<IconWallet size={12} stroke={1.5} />}
              size="xs"
              variant="light"
              color="purple"
              style={{ cursor: 'pointer' }}
            >
              {t(i18nKeys.PAYMENT_PLAN.TITLE.ONE)}
            </Badge>
          )}
        </Group>
      </Stack>
    </Group>
  );
};
